import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, X } from "@phosphor-icons/react";
import { api, fileUrl, HERO_IMG } from "../../lib/api";
import { useSite } from "../../lib/site";
import { getBootstrap } from "../../lib/bootstrap";
import SEO from "../../components/SEO";
import CTABand from "../../components/public/CTABand";
import VideoEmbed from "../../components/public/VideoEmbed";

export default function BlogPost() {
  const { slug } = useParams();
  const site = useSite();
  const [post, setPost] = useState(() => {
    const boot = getBootstrap("blog");
    if (Array.isArray(boot)) return boot.find((p) => p.slug === slug) || null;
    return null;
  });
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch((e) => {
      if (e?.response?.status === 404 && !post) setNotFound(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-max py-32">
        <h1 className="h-section">Post not found</h1>
        <Link to="/blog" className="btn-outline mt-6">Back to journal</Link>
      </div>
    );
  }
  if (!post) return <div className="container-max py-32 text-atc-muted">Loading…</div>;

  return (
    <div data-testid="blog-post-page">
      <SEO
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.excerpt || post.body.slice(0, 160)}
        image={post.cover_image ? fileUrl(post.cover_image) : undefined}
        type="article"
        publishedTime={post.created_at}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt || post.body.slice(0, 160),
          "datePublished": post.created_at,
          "image": post.cover_image ? fileUrl(post.cover_image) : undefined,
          "publisher": { "@type": "Organization", "name": site?.business_name || "ATC Home Pro's" },
        }}
      />
      <section className="relative bg-atc-black text-white">
        <div className="absolute inset-0">
          <img src={fileUrl(post.cover_image) || HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-atc-black/70 via-atc-black/70 to-atc-black" />
        </div>
        <div className="container-max relative py-28">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-white font-heading uppercase tracking-widest text-xs mb-6" data-testid="back-to-blog">
            <ArrowLeft size={14} weight="bold" /> Journal
          </Link>
          <div className="overline mb-4 text-atc-red">{new Date(post.created_at).toLocaleDateString()}</div>
          <h1 className="h-display max-w-4xl">{post.title}</h1>
          {post.excerpt && <p className="mt-6 text-white/80 text-xl max-w-2xl">{post.excerpt}</p>}
        </div>
      </section>

      <section className="section-pad bg-atc-paper">
        <div className="container-max max-w-3xl">
          <article className="prose prose-lg max-w-none text-atc-charcoal font-body whitespace-pre-line">
            {post.body}
          </article>
        </div>
      </section>

      {(post.video_urls?.length || 0) > 0 && (
        <section className="pb-24 bg-atc-paper" data-testid="post-videos">
          <div className="container-max max-w-3xl">
            <div className="overline mb-4">Watch</div>
            <div className="space-y-6">
              {post.video_urls.map((url, i) => (
                <VideoEmbed key={i} url={url} title={post.title} />
              ))}
            </div>
          </div>
        </section>
      )}

      {(post.images?.length || 0) > 0 && (
        <section className="pb-24 bg-atc-paper" data-testid="post-photos">
          <div className="container-max">
            <div className="overline mb-4">Photos</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {post.images.map((path, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(path)}
                  className="group relative block border-2 border-atc-black bg-atc-black overflow-hidden"
                  data-testid={`post-photo-${i}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={fileUrl(path)} alt="" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-500 group-hover:scale-105" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-atc-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="post-lightbox">
          <div className="relative max-w-5xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={fileUrl(lightbox)} alt="" className="w-full h-full object-contain" />
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 bg-white text-atc-black p-2 hover:bg-atc-red hover:text-white" data-testid="close-post-lightbox">
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>
      )}

      <CTABand phone={site?.phone} />
    </div>
  );
}
